import { useState, useEffect, useCallback } from 'react';
import type { BeforeInstallPromptEvent, InstallPromptResult } from '@core/types/pwa.types';
import type { NavigatorWithStandalone } from '@core/types/common.types';

const DISMISS_KEY = 'pwa-install-dismissed';
const DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 días en ms

/**
 * Hook para gestionar el prompt de instalación de la PWA
 * 
 * @returns Objeto con estado y métodos para controlar la instalación
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { canInstall, isInstalled, promptInstall } = useInstallPrompt();
 *   
 *   if (isInstalled) return null;
 *   
 *   return (
 *     <button onClick={promptInstall} disabled={!canInstall}>
 *       Instalar App
 *     </button>
 *   );
 * }
 * ```
 */
export function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  // Verificar si la app ya está instalada
  useEffect(() => {
    const checkInstalled = () => {
      // Detectar si estamos en modo standalone (app instalada)
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
      const navWithStandalone = window.navigator as NavigatorWithStandalone;
      const isIOSStandalone = 'standalone' in window.navigator && navWithStandalone.standalone === true;
      
      setIsInstalled(isStandalone || isIOSStandalone);
    };

    checkInstalled();
    
    // Escuchar cambios (por si se instala/desinstala durante la sesión)
    window.matchMedia('(display-mode: standalone)').addEventListener('change', checkInstalled);
    
    return () => {
      window.matchMedia('(display-mode: standalone)').removeEventListener('change', checkInstalled);
    };
  }, []);

  // Verificar si el banner fue descartado recientemente
  useEffect(() => {
    const dismissedData = localStorage.getItem(DISMISS_KEY);
    
    if (dismissedData) {
      try {
        const { timestamp } = JSON.parse(dismissedData);
        const elapsed = Date.now() - timestamp;
        
        if (elapsed < DISMISS_DURATION) {
          setIsDismissed(true);
        } else {
          // Expiró el período de dismiss, limpiar
          localStorage.removeItem(DISMISS_KEY);
        }
      } catch (error) {
        // Si hay error parseando, limpiar
        localStorage.removeItem(DISMISS_KEY);
      }
    }
  }, []);

  // Capturar el evento beforeinstallprompt
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevenir que el browser muestre su propio prompt
      e.preventDefault();
      
      // Guardar el evento para usarlo después
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  /**
   * Muestra el prompt de instalación nativo
   */
  const promptInstall = useCallback(async (): Promise<InstallPromptResult> => {
    if (!deferredPrompt) {
      return {
        outcome: 'error',
        error: new Error('Install prompt not available'),
      };
    }

    try {
      // Mostrar el prompt
      await deferredPrompt.prompt();

      // Esperar la respuesta del usuario
      const choiceResult = await deferredPrompt.userChoice;

      // Limpiar el prompt usado
      setDeferredPrompt(null);

      // Si el usuario aceptó, marcar como instalado
      if (choiceResult.outcome === 'accepted') {
        setIsInstalled(true);
      }

      return {
        outcome: choiceResult.outcome,
        platform: choiceResult.platform,
      };
    } catch (error) {
      return {
        outcome: 'error',
        error: error as Error,
      };
    }
  }, [deferredPrompt]);

  /**
   * Descarta el banner de instalación por 7 días
   */
  const dismissPrompt = useCallback(() => {
    const dismissData = {
      timestamp: Date.now(),
    };
    
    localStorage.setItem(DISMISS_KEY, JSON.stringify(dismissData));
    setIsDismissed(true);
  }, []);

  /**
   * Limpia el dismiss para mostrar el banner de nuevo
   */
  const clearDismiss = useCallback(() => {
    localStorage.removeItem(DISMISS_KEY);
    setIsDismissed(false);
  }, []);

  return {
    /**
     * Si el prompt de instalación está disponible
     */
    canInstall: !!deferredPrompt && !isInstalled,
    
    /**
     * Si la app ya está instalada
     */
    isInstalled,
    
    /**
     * Si el usuario ha descartado el banner recientemente
     */
    isDismissed,
    
    /**
     * Muestra el prompt de instalación
     */
    promptInstall,
    
    /**
     * Descarta el banner por 7 días
     */
    dismissPrompt,
    
    /**
     * Limpia el dismiss
     */
    clearDismiss,
  };
}
